import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const RegisterPage = () => {
  const { register } = useAuth();
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    const res = await register(email.trim(), password, name.trim());
    setLoading(false);
    if (res.ok) {
      const linked = res.user?.linked_orders || 0;
      if (linked > 0) {
        toast.success(`Account created — we linked ${linked} prior order${linked === 1 ? "" : "s"} to it.`);
      } else {
        toast.success("Account created.");
      }
      nav("/account", { replace: true });
    } else {
      setErr(res.error || "Could not create account.");
    }
  };

  return (
    <div className="px-4 md:px-8 py-12 md:py-20 min-h-[70vh]" data-testid="register-page">
      <div className="max-w-md mx-auto border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard p-6 md:p-10">
        <p className="font-condensed text-sm uppercase tracking-widest text-[#D95D39]">// new arrival</p>
        <h1 className="font-display text-4xl md:text-5xl uppercase leading-none mt-2">
          Make an account.
        </h1>
        <p className="font-body text-base mt-3 text-[#1C1914]/80">
          We'll link any previous orders made with the same email. Magic, basically.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="block font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-1">Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-duds"
              data-testid="register-name"
            />
          </div>
          <div>
            <label className="block font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-duds"
              data-testid="register-email"
            />
          </div>
          <div>
            <label className="block font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-1">
              Password <span className="opacity-50">(8+ characters)</span>
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-duds"
              data-testid="register-password"
            />
          </div>
          {err && (
            <div className="border-2 border-[#D95D39] bg-[#D95D39]/10 px-3 py-2 font-condensed text-sm uppercase tracking-widest text-[#D95D39]" data-testid="register-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="btn-duds w-full flex items-center justify-center gap-2"
            data-testid="register-submit"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Create account
          </button>
        </form>

        <div className="mt-6 font-condensed text-xs uppercase tracking-widest text-center">
          <Link to="/login" className="text-[#1C1914]/70 hover:text-[#D95D39]" data-testid="register-login-link">
            Already have one? Log in →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
